import { Loading, ProgressBar, Button } from '@arcadiapower/shrike';
import { OneColumnGrid } from '~/components/layout';
import { Title, Description } from '~/components/typography';
import { useCopy } from '~/hooks';

export const DocuSign = ({ childStep, context, next, overrideCopy }: DocuSignProps) => {
  const copy = useCopy(context, defaultCopy, overrideCopy);
  return (
    <OneColumnGrid textAlign="center" testId="screen-DocuSign">
      {childStep === 'polling' && (
        <>
          <Title>{copy.polling.title}</Title>
          <Description mt={6}>{copy.polling.description}</Description>
          <ProgressBar maxValue={1000 * 180} secondsToProgress={20} automatic />
        </>
      )}
      {childStep === 'timeout' && (
        <>
          <Title>{copy.timeout.title}</Title>
          <Description mt={6}>{copy.timeout.description}</Description>
          <Button mode="primary" onClick={next} data-testid="docusign-timeout-button">
            {copy.timeout.submitButton}
          </Button>
        </>
      )}
      {childStep === 'ready' && <Loading />}
      {childStep === 'completed' && <Loading />}
      <div data-testid={childStep} />
    </OneColumnGrid>
  );
};

export const defaultCopy = (context: DocuSignContext) => {
  switch (context.useCase) {
    case 'ipa':
      return ipaCopy;
    case 'nm':
      return nmCopy;
    case 'ma':
      return maCopy;
    case 'usage_request':
      return xcelMNCopy;
    case 'mn_lmi':
      return mnLmiCopy;
    case 'il':
      return ilCopy;
    default:
      return lmiCopy;
  }
};

export type DocuSignCopy = ReturnType<typeof defaultCopy>;

const ipaCopy = {
  polling: {
    title: "Don't close this window yet, we're preparing your community solar disclosure form...",
    description:
      'This is legally required by the state of Illinois to complete your enrollment, and it could take up to 3 minutes to prepare.',
  },
  timeout: {
    title: 'One final step!',
    description:
      "It's taking longer than expected to prepare your community solar disclosure form, so rather than have you wait we'll email you once it's ready. It's the last step before we can get you on a community solar farm!",
    submitButton: 'Continue',
  },
};

const nmCopy = {
  polling: {
    title: "Don't close this window yet, we're preparing your community solar forms...",
    description: 'This is legally required by the state of New Mexico to complete your enrollment.',
  },
  timeout: {
    title: 'One final step!',
    description:
      "It's taking longer than expected to prepare your community solar forms, so rather than have you wait we'll email you once it's ready. It's the last step before we can get you on a community solar farm!",
    submitButton: 'Go to Dashboard',
  },
};

const maCopy = {
  polling: {
    title: "Don't close this window yet, we're preparing your community solar forms...",
    description:
      'This is legally required by the state of Massachusetts to complete your enrollment.',
  },
  timeout: {
    title: 'One final step!',
    description:
      "It's taking longer than expected to prepare your community solar forms, so rather than have you wait we'll email you once it's ready. It's the last step before we can get you on a community solar farm!",
    submitButton: 'Go to dashboard',
  },
};

const xcelMNCopy = {
  polling: {
    title: "Don't close this window yet, we're preparing your utility data consent...",
    description:
      'Since we were unable to successfully connect to your Xcel utility account, this form allows us to request your history usage from the utility.',
  },
  timeout: {
    title: 'One final step!',
    description:
      "It's taking longer than expected to prepare your utility data consent, so rather than have you wait we'll email you once it's ready. It's the last step before we can get you on a community solar farm!",
    submitButton: 'Continue',
  },
};

const ilCopy = {
  polling: {
    title: "Don't close this window yet, we're preparing your IL disclosure form...",
    description: '',
  },
  timeout: {
    title: 'One final step!',
    description:
      "It's taking longer than expected to prepare your IL disclosure form, so rather than have you wait we'll email you once it's ready.",
    submitButton: 'Continue',
  },
};

const lmiCopy = {
  polling: {
    title: "Don't close this window yet, we're preparing your income status verification form...",
    description: '',
  },
  timeout: {
    title: 'One final step!',
    description:
      "It's taking longer than expected to prepare your income status verification form, so rather than have you wait we'll email you once it's ready.",
    submitButton: 'Continue',
  },
};
const mnLmiCopy = {
  polling: {
    title: "Don't close this window yet, we're preparing your income-based eligibility form...",
    description:
      'If we were unable to successfully connect to your Xcel account, you will also be asked to sign a utility data consent form.',
  },
  timeout: {
    title: 'One final step!',
    description:
      "It's taking longer than expected to prepare your income-based eligibility form, so rather than have you wait we'll email you once it's ready.",
    submitButton: 'Continue',
  },
};

export interface DocuSignProps {
  childStep: 'polling' | 'timeout' | 'ready' | 'completed';
  context: DocuSignContext;
  next?: () => void;
  overrideCopy?: (context: DocuSignContext) => Partial<ReturnType<typeof defaultCopy>>;
}

export interface DocuSignContext {
  useCase: 'lmi' | 'ipa' | 'nm' | 'ma' | 'usage_request' | 'mn_lmi' | 'il';
  user: {
    id: number;
  };
}
